import { useState, useEffect, useContext, useRef } from "react";
import { UserContext } from "../../Context";
import { useNavigate, useLocation } from "react-router-dom";
import { CommonTable, Loader, Modal, Tooltip } from "../../Components/Common";
import { FiInfo, FiFilter, FiDownload, FiEdit, FiUpload, FiFileText, FiCheckCircle } from "react-icons/fi";
import {
  SALARY_SLIP_LABELS,
  SALARY_SLIP_COLUMNS,
  getSlipStatusColor,
  SALARY_SLIP_STATUS,
  SALARY_SLIP_STATUS_OPTIONS,
  ROLES,
} from "./constants";
import { formatCurrency, formatMonthYear, toProperCase } from "../../Utils";
import { usePayroll } from "./Hooks/usePayroll.js";
import { RESET_PAGINATION } from "../../Constants/Strings";
import { DETAIL_SALARY_SLIP, EDIT_SALARY_SLIP } from "../../routes.js";
import InfiniteScroll from "react-infinite-scroll-component";
import CommonFilter from "../../Components/Common/CommonFilter";
import { useSalarySlipPdf } from "./Hooks/useSalarySlipPdf";
import IRComplianceModal, { REPORT_ROLES } from "./IRComplianceModal";

// 10 rows don't fill the 70vh scroll area on large screens, so no scroll
// event fires and InfiniteScroll never requests the next page.
const SALARY_SLIP_PAGE_SIZE = 25;
// Upload / edit; Accounts views every slip (REPORT_ROLES) but can't change them
const SALARY_SLIP_MANAGER_ROLES = [ROLES.HR, ROLES.DIRECTOR_MANAGEMENT];
// HR sends slips for approval; only Director approves
const SALARY_SLIP_APPROVER_ROLES = [ROLES.DIRECTOR_MANAGEMENT];

const SalarySlipList = () => {
  const location = useLocation();
  const {
    loading,
    filters,
    setFilters,
    setPagination,
    pagination,
    payroll,
    setPayroll,
    fetchSalarySlips,
    loadMoreSalarySlips,
    addSalarySlip,
    notifyDownloadSalarySlip,
    updateSalarySlip,
    downloadSampleTemplate,
    uploadSalaryWorkbook,
  } = usePayroll(location.state?.filters || { status: "", user: "" });
  const fileInputRef = useRef(null);

  const handleSalaryFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const success = await uploadSalaryWorkbook(file);
    if (success) {
      loadSalarySlip();
    }
    e.target.value = "";
  };
  const [selectedUserOption, setSelectedUserOption] = useState(() => {
    return (
      location.state?.selectedUser || null
    );
  });
  const [tempFilters, setTempFilters] = useState(filters);
  const [showFilter, setShowFilter] = useState(false);
  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0"
    )}`;
  };
  const { downloadSalarySlipPdf } = useSalarySlipPdf();
  const [payrollMonth, setPayrollMonth] = useState(getCurrentMonth());

  const handleRunPayroll = async () => {
    if (!payrollMonth) return;

    const success = await addSalarySlip({
      month: payrollMonth, // "YYYY-MM"
    });

    if (success) {
      loadSalarySlip();
    }
  };

  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [showIRReports, setShowIRReports] = useState(false);
  const [slipToApprove, setSlipToApprove] = useState(null);
  const [approving, setApproving] = useState(false);
  const loadSalarySlip = async () => {
    const data = await fetchSalarySlips({
      offset: 0,
      limit: SALARY_SLIP_PAGE_SIZE,
    });
    setPayroll(data);
  };

  const handleSlipDownload = async (slipId) => {
    await notifyDownloadSalarySlip(slipId);
  };

  const canApprove = (row) =>
    row.status === SALARY_SLIP_STATUS.PENDING_FOR_APPROVAL &&
    SALARY_SLIP_APPROVER_ROLES.includes(user?.role);

  const handleApprove = async () => {
    setApproving(true);
    const updated = await updateSalarySlip(slipToApprove.id, {}, { action: "approve" });
    setApproving(false);
    setSlipToApprove(null);
    if (updated) {
      // Update the row in place so the list keeps its scroll position
      setPayroll((prev) =>
        prev.map((slip) => (slip.id === updated.id ? { ...slip, status: updated.status } : slip))
      );
    }
  };

  const renderField = (key, row) => {
    if (key === "status") {
      return (
        <span
          className={`px-1.5 py-1 rounded-md text-xs font-medium ${getSlipStatusColor(
            row.status
          )}`}
        >
          {toProperCase(row.status)}
        </span>
      );
    }
    if (key === "month") {
      if (!row.start_date) return "-";
      return new Date(row.start_date).toLocaleString("en-US", {
        month: "long",
        year: "numeric",
      });
    }
    if (key === "action") {
      return (
        <div className="flex items-center gap-2">
          {/* Detail Button */}
          <Tooltip text={"View Salary Details"}>
            <button
              onClick={() => handleDetail(row.id)}
              className="h-5 w-5 text-green-500 cursor-pointer hover:text-green-700 transition-colors"
            >
              <FiInfo size={17} />
            </button>
          </Tooltip>
          <Tooltip text={"Download Salary Slip"}>
            <button
              className="h-5 w-5 text-green-500 cursor-pointer hover:text-green-700 transition-colors"
              onClick={() => {
                downloadSalarySlipPdf(row);
                handleSlipDownload(row.id);
              }}
            >
              <FiDownload className="text-md" />
            </button>
          </Tooltip>
          {(user?.role === ROLES.HR || user?.role === ROLES.DIRECTOR_MANAGEMENT) && (
            <Tooltip
              text={`${row.status !== SALARY_SLIP_STATUS.APPROVED
                  ? "Edit Salary Details"
                  : "Can not be edited in approved state"
                }`}
            >
              <button
                disabled={row.status === SALARY_SLIP_STATUS.APPROVED}
                className={`h-5 w-5 cursor-pointer transition-colors 
                  ${row.status !== SALARY_SLIP_STATUS.APPROVED
                    ? "text-green-500 hover:text-green-700"
                    : "text-gray-400 cursor-not-allowed"
                  }`}
                onClick={() => row.status !== SALARY_SLIP_STATUS.APPROVED && handleEdit(row.id)}
              >
                <FiEdit className="text-md" />
              </button>
            </Tooltip>
          )}
          {canApprove(row) && (
            <Tooltip text={"Approve Salary Slip"}>
              <button
                className="h-5 w-5 text-green-500 cursor-pointer hover:text-green-700 transition-colors"
                onClick={() => setSlipToApprove(row)}
              >
                <FiCheckCircle className="text-md" />
              </button>
            </Tooltip>
          )}
        </div>
      );
    }
    return row[key] || "-";
  };

  const renderMobileCards = () => {
    return (
      <div className="md:hidden space-y-2 mt-2">
        {payroll.length === 0 ? (
          <p className="text-center text-gray-600">No Slips found.</p>
        ) : (
          payroll.map((row) => {
            return (
              <div
                key={row.id}
                className="border rounded-lg shadow-sm p-2 bg-white"
              >
                <div className="mb-2">
                  <p className="font-semibold text-primary flex justify-between">
                    <span> {row.username}</span>
                    <span>{formatCurrency(row.net)}</span>
                  </p>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-900 font-normal">
                      {formatMonthYear(row.start_date)}
                    </span>
                  </div>
                  <div className="font-semibold text-primary flex justify-between items-center">
                    <span
                      className={` rounded-md text-sm mt-2 font-medium ${getSlipStatusColor(
                        row.status
                      )}`}
                    >
                      {toProperCase(row.status)}
                    </span>
                    <div className="flex items-center gap-3">
                      <FiInfo
                        size={17}
                        onClick={() => handleDetail(row.id)}
                        className="h-5 w-5 text-green-500 cursor-pointer hover:text-green-700 transition-colors"
                      />
                      <button
                        className="flex items-center gap-1 text-gray-600"
                        onClick={() => {
                          downloadSalarySlipPdf(row);
                          handleSlipDownload(row.id);
                        }}
                      >
                        <FiDownload className="text-md" />
                      </button>
                      {SALARY_SLIP_MANAGER_ROLES.includes(user?.role) && (
                          <button
                            disabled={row.status === SALARY_SLIP_STATUS.APPROVED}
                            className={`h-5 w-5 cursor-pointer transition-colors
                            ${row.status !== SALARY_SLIP_STATUS.APPROVED
                                ? "text-green-500 hover:text-green-700"
                                : "text-gray-400 cursor-not-allowed"
                              }`}
                            onClick={() =>
                              row.status !== SALARY_SLIP_STATUS.APPROVED &&
                              handleEdit(row.id)
                            }
                          >
                            <FiEdit className="text-md" />
                          </button>
                        )}
                      {canApprove(row) && (
                        <button
                          className="h-5 w-5 text-green-500 cursor-pointer hover:text-green-700 transition-colors"
                          onClick={() => setSlipToApprove(row)}
                          aria-label="Approve Salary Slip"
                        >
                          <FiCheckCircle className="text-md" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  // Carried to the detail/edit page and handed back on return so the list
  // reopens with the same filters.
  const filterState = { filters, selectedUser: selectedUserOption };

  const handleDetail = (id) => {
    navigate(DETAIL_SALARY_SLIP.replace(":slipId", id), { state: filterState });
  };

  const handleEdit = (id) => {
    navigate(EDIT_SALARY_SLIP.replace(":slipId", id), { state: filterState });
  };

  // The dropdown works with the user object, while `filters` holds only the id.
  const openFilters = () => {
    setTempFilters({ ...filters, user: selectedUserOption });
    setShowFilter(true);
  };

  const handleApplyFilters = () => {
    // "All Users" comes back as { id: null }
    const selectedUser = tempFilters.user?.id ? tempFilters.user : null;
    const applied = { ...tempFilters, user: selectedUser?.id || "" };
    setSelectedUserOption(selectedUser);
    setFilters(applied);
    setPagination(RESET_PAGINATION);
    setPayroll([]);
    setShowFilter(false);

    // Update navigation state for back button
    navigate(".", { state: { filters: applied, selectedUser }, replace: true });
  };
  const handleClearFilters = () => {
    const cleared = { status: "", user: "" };
    setTempFilters(cleared);
    setFilters(cleared);
    setSelectedUserOption(null);
    setPagination(RESET_PAGINATION);
    setPayroll([]);
    setShowFilter(false);

    navigate(".", { state: { filters: cleared, selectedUser: null }, replace: true });
  };

  const handleCancelFilters = () => {
    setShowFilter(false);
  };

  useEffect(() => {
    loadSalarySlip();
  }, [filters]);

  return (
    <div className="px-4 py-4">
      <div className="flex flex-col w-full">
        {/* Heading + Add button */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-semibold text-primary">Salary Slip</h2>
          <div className="flex items-center gap-2">
            {SALARY_SLIP_MANAGER_ROLES.includes(user.role) && (
              <>
              <button
                className="px-3 py-2 rounded bg-primary hover:bg-green-600 text-white font-medium shadow-md flex items-center gap-1.5 text-sm"
                onClick={downloadSampleTemplate}
                title="Download Sample Excel Template"
              >
                <FiDownload className="text-lg" />
                <span className="hidden sm:inline">Download Sample</span>
              </button>

              <button
                className="px-3 py-2 rounded bg-primary hover:bg-green-600 text-white font-medium shadow-md flex items-center gap-1.5 text-sm"
                onClick={() => fileInputRef.current?.click()}
                title="Upload Salary Slip Excel"
              >
                <FiUpload className="text-lg" />
                <span className="hidden sm:inline">Upload Excel</span>
              </button>

              <input
                type="file"
                ref={fileInputRef}
                accept=".xlsx, .xls"
                onChange={handleSalaryFile}
                className="hidden"
              />
              </>
            )}

            {REPORT_ROLES.includes(user.role) && (
              <button
                className="px-3 py-2 rounded bg-primary hover:bg-green-600 text-white font-medium shadow-md flex items-center gap-1.5 text-sm"
                onClick={() => setShowIRReports(true)}
                title="IR Compliance Reports"
              >
                <FiFileText className="text-lg" />
                <span className="hidden sm:inline">IR Reports</span>
              </button>
            )}

            {REPORT_ROLES.includes(user.role) && (
              <button
                className="px-4 py-2 rounded bg-primary hover:bg-green-600 text-white font-bold shadow-lg flex items-center gap-1"
                onClick={openFilters}
              >
                <FiFilter className="text-xl" />
              </button>
            )}
          </div>
        </div>

        {showIRReports && (
          <IRComplianceModal onClose={() => setShowIRReports(false)} />
        )}

        <Modal
          isOpen={Boolean(slipToApprove)}
          title="Approve Salary Slip"
          maxWidthClass="max-w-sm"
          footer={
            <>
              <button
                className="px-4 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                onClick={() => setSlipToApprove(null)}
                disabled={approving}
              >
                Cancel
              </button>
              <button
                className="px-6 py-2 rounded-md text-sm font-medium text-white bg-primary hover:bg-green-600 disabled:bg-gray-400"
                onClick={handleApprove}
                disabled={approving}
              >
                {approving ? "Approving..." : "Approve"}
              </button>
            </>
          }
        >
          {slipToApprove && (
            <p className="text-sm text-gray-600 text-center">
              Approve the {formatMonthYear(slipToApprove.start_date)} salary slip of{" "}
              <span className="font-semibold">{slipToApprove.username}</span> for{" "}
              <span className="font-semibold">{formatCurrency(slipToApprove.net)}</span>?
              Approved slips can no longer be edited.
            </p>
          )}
        </Modal>

        {/* Filter Modal */}
        {showFilter && (
          <CommonFilter
            title="Salary Slip Filter"
            tempFilters={tempFilters}
            setTempFilters={setTempFilters}
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
            onCancel={handleCancelFilters}
            statusOptions={[
              { label: "All", value: "" },
              ...SALARY_SLIP_STATUS_OPTIONS,
            ]}
            useSearchableUser={true}
            selectedSearchableValue={selectedUserOption}
          />
        )}
      </div>
      {(user.role === ROLES.HR || user.role === ROLES.DIRECTOR_MANAGEMENT) && (
        <div className="flex gap-2 mb-1">
          <input
            type="month"
            value={payrollMonth}
            onChange={(e) => setPayrollMonth(e.target.value)}
            className="border px-2 py-1 h-10"
          />
          <button
            className="px-4 py-2 bg-green-500 text-white mb-1 h-10"
            onClick={handleRunPayroll}
          >
            Run Payroll
          </button>
        </div>
      )}
      <div
        className="h-[70vh] overflow-x-auto overflow-y-auto px-4 py-2 mt-3"
        id="scrollDiv"
      >
        {/* Full-page loader only for a fresh list. `loading` is also set by
            load-more and slip download; replacing the rows then would reset
            the scroll back to the first user. */}
        {loading && payroll.length === 0 ? (
          <div className="flex justify-center items-center h-full">
            <Loader />
          </div>
        ) : (
          <InfiniteScroll
            dataLength={payroll.length}
            next={loadMoreSalarySlips}
            hasMore={pagination.offset < pagination.count}
            scrollableTarget="scrollDiv"
            loader={
              <p className="text-center text-sm text-gray-500 py-2">
                Loading...
              </p>
            }
          >
            <div className="hidden md:block">
              <CommonTable
                tableColumns={SALARY_SLIP_COLUMNS}
                columnLabels={SALARY_SLIP_LABELS}
                tableRows={payroll}
                renderField={renderField}
                headerClassName="bg-gray-100"
                rowClick={null}
              />
            </div>
            {renderMobileCards()}
          </InfiniteScroll>
        )}
      </div>
      {/* Mobile Card View */}
    </div>
  );
};
export default SalarySlipList;
